import React, { useEffect, useRef, useState } from 'react';

import { PrimaryButton } from '@components/Button/variants';
import IconDeepResearchDoubleSparkle from '@components/Images/DeepResearch/IconDeepResearchDoubleSparkle';
import IconArrowDown from '@components/Images/Rebranding/IconArrowDown';
import { SUGGESTED_PROMPT_QUERY_PARAM } from '@components/pages/AI/DeepResearch/constants';
import useEventTracking from '@hooks/useEventTracking';
import { deep_research_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

interface ExpandableFreePreviewProps {
  freePreview: string;
  prompt: string;
}

const CLICK_TEXT_VIEW_RESPONSE = 'View Response';
const CLICK_TEXT_TRY_DEEP_RESEARCH = 'Try Deep Research';

const ExpandableFreePreview = ({ freePreview, prompt }: ExpandableFreePreviewProps) => {
  const { trackEvent } = useEventTracking();
  const [isExpanded, setIsExpanded] = useState(false);
  const expandingDivRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const newHeight =
      isExpanded && expandingDivRef.current ? expandingDivRef.current?.scrollHeight : 0;
    setHeight(newHeight);
  }, [isExpanded]);

  const handleViewResponseClick = () => {
    if (!isExpanded) {
      trackEvent(click({ clickTarget: ClickTarget.Button, clickText: CLICK_TEXT_VIEW_RESPONSE }));
      setIsExpanded(true);
    }
  };

  return (
    <>
      <div
        style={{
          height: `${height}px`,
          transition: 'height 0.5s ease-in-out',
          overflow: 'hidden',
        }}
        className="px-0 sm:px-24"
      >
        <div ref={expandingDivRef}>
          <IconDeepResearchDoubleSparkle className="mt-16 h-16 w-16 text-pro-gold" />
          <p className="!m-0 border-0 border-b border-solid border-grayscale-gray pb-20 !font-suisse-screen !text-byline-md text-black lg:pb-28">
            {AuthoredContentReactToHtmlParser(freePreview)}
          </p>
        </div>
      </div>
      {isExpanded ? (
        <PrimaryButton
          as="link"
          size="small"
          className="mx-auto mt-20 items-center gap-10 !text-body-md !no-underline"
          href={deep_research_path({ [SUGGESTED_PROMPT_QUERY_PARAM]: prompt })}
          onClickCapture={(event: React.MouseEvent<HTMLAnchorElement>) => {
            event.stopPropagation();
            trackEvent(
              click({
                clickTarget: ClickTarget.Button,
                clickText: CLICK_TEXT_TRY_DEEP_RESEARCH,
              })
            );
          }}
        >
          <IconDeepResearchDoubleSparkle className="fill-current" height={22} width={22} />
          <span>{CLICK_TEXT_TRY_DEEP_RESEARCH}</span>
          <IconArrowDown className="-rotate-90 fill-current" height={14} width={14} />
        </PrimaryButton>
      ) : (
        <PrimaryButton
          as="button"
          size="small"
          className="mx-auto mt-20 items-center gap-10 !text-body-md"
          onClick={handleViewResponseClick}
        >
          <span>{CLICK_TEXT_VIEW_RESPONSE}</span>
          <IconArrowDown className="fill-current" height={14} width={14} />
        </PrimaryButton>
      )}
    </>
  );
};

export default ExpandableFreePreview;
